<template>
  <transition name="fade" appear>
    <div class="login-page">
      <form @submit.prevent="handleSubmit" v-if="!isLoading">
        <h3>Reset Your Password</h3>

        <div
          class="error"
          v-if="errors.general || errors.email || errors.token"
        >
          {{ errors.general || errors.email || errors.token }}
        </div>

        <div class="success" v-if="successMessage">
          {{ successMessage }}
          <p>You will be redirected to login in a moment...</p>
        </div>

        <div v-if="!successMessage">
          <h6>New Password</h6>
          <input
            type="password"
            v-model="password"
            placeholder="Enter your new password"
            required
            minlength="8"
            ref="passwordInput"
          />

          <div class="error" v-if="errors.password">
            {{ errors.password }}
          </div>

          <h6>Confirm Password</h6>
          <input
            type="password"
            v-model="passwordConfirmation"
            placeholder="Confirm your new password"
            required
            minlength="8"
          />

          <button type="submit" class="logout pixel-border">
            <span class="pixel"> Reset Password </span>
          </button>
        </div>

        <div class="forgot-password">
          <router-link to="/login" class="link-button">
            Back to Login
          </router-link>
        </div>
      </form>

      <div class="loader-general" v-else>
        <h3>Resetting your password</h3>
        <svg
          version="1.1"
          id="L5"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          x="0px"
          y="0px"
          viewBox="0 0 100 100"
          enable-background="new 0 0 0 0"
          xml:space="preserve"
        >
          <circle fill="#000" stroke="none" cx="6" cy="50" r="6">
            <animateTransform
              attributeName="transform"
              dur="1s"
              type="translate"
              values="0 15 ; 0 -15; 0 15"
              repeatCount="indefinite"
              begin="0.1"
            />
          </circle>
          <circle fill="#000" stroke="none" cx="30" cy="50" r="6">
            <animateTransform
              attributeName="transform"
              dur="1s"
              type="translate"
              values="0 10 ; 0 -10; 0 10"
              repeatCount="indefinite"
              begin="0.2"
            />
          </circle>
          <circle fill="#000" stroke="none" cx="54" cy="50" r="6">
            <animateTransform
              attributeName="transform"
              dur="1s"
              type="translate"
              values="0 5 ; 0 -5; 0 5"
              repeatCount="indefinite"
              begin="0.3"
            />
          </circle>
        </svg>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePasswordReset } from '@/composables/usePasswordReset';
import { useGameAudioStore } from '@/stores/gameAudioStore';

const route = useRoute();
const router = useRouter();
const { resetPassword, errors, successMessage, isLoading } = usePasswordReset();

const token = ref('');
const email = ref('');
const password = ref('');
const passwordConfirmation = ref('');
const passwordInput = ref(null);

onMounted(() => {
  // Extract token from route params and email from query parameters
  token.value = route.params.token || '';
  email.value = route.query.email || '';

  console.log('Password reset URL params:', {
    token: token.value,
    email: email.value,
    params: route.params,
    query: route.query,
  });

  // If missing required parameters, redirect to login
  if (!token.value || !email.value) {
    console.error('Missing token or email in password reset URL');
    console.error('Expected: token from params, email from query');
    console.error('Got:', { token: token.value, email: email.value });
    router.push('/login');
    return;
  }

  if (passwordInput.value) {
    passwordInput.value.focus();
  }
});

const handleSubmit = async () => {
  const result = await resetPassword(
    token.value,
    email.value,
    password.value,
    passwordConfirmation.value
  );

  if (result.success) {
    const gameAudio = useGameAudioStore();
    gameAudio.playSpeech(
      '/assets/games/audio/system-you-will-be-redirected-t.mp3'
    );
    // Redirect to login page after successful reset
    setTimeout(() => {
      router.push('/login');
    }, 3000);
  }
};
</script>

<style scoped lang="scss">
@use '@/css/components/login.scss';

.success {
  color: green;
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f0f8f0;
  border: 1px solid #4caf50;
  border-radius: 4px;
  width: 100%;
}

.error {
  color: red;
  margin-bottom: 15px;
  width: 100%;
  justify-self: baseline;
  border-radius: 5px;
}

.forgot-password {
  text-align: center;
  margin-top: 25px;
}

.link-button {
  background: none;
  border: none;
  color: #800000;
  text-decoration: none;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  position: relative;
  transition: all 0.3s ease;
}

.link-button::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 1px;
  background-color: #600000;
  transition: width 0.3s ease;
}

.link-button:hover {
  color: #600000;
  transform: translateY(-3px);
}

.link-button:hover::after {
  width: 100%;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
